import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
// jwt-decode only exports a named function; there is no default export in the
// package.  The former import style used throughout the repo is correct.
import { jwtDecode } from 'jwt-decode';
import {
  FiGrid, FiList, FiPhone, FiMail, FiMapPin,
  FiCalendar, FiClock, FiCheckCircle, FiEdit2, FiTrash2, FiX
} from 'react-icons/fi';

const FranchiseCompanies = () => {
  const { franchiseId } = useParams();
  const navigate = useNavigate();

  const [franchise, setFranchise] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [currentUser, setCurrentUser] = useState(null);

  // Edit Company Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editFormData, setEditFormData] = useState({
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    emergencyMobile: '',
    address: '',
    createdDate: '',
    startDate: '',
    endDate: '',
    weeklyHolidays: [],
    businessSubscriptionPlan: ''
  });

  // Decode JWT token to get user role
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser({
          id: decoded.id || decoded.userId || decoded._id,
          role: decoded.role
        });
      } catch (error) {
        console.error('Error decoding JWT:', error);
      }
    }
  }, []);

  // memoize to satisfy react-hooks/exhaustive-deps and avoid rewriting
  // the function on every render.  fetchFranchiseAndCompanies will change when
  // franchiseId changes, which is what we want.
  const fetchFranchiseAndCompanies = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      // Fetch franchise details
      const franchiseResponse = await axios.get(`https://slateblue-mallard-430836.hostingersite.com/api/franchises/${franchiseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFranchise(franchiseResponse.data);

      // Fetch companies for this franchise
      const companiesResponse = await axios.get(`https://slateblue-mallard-430836.hostingersite.com/api/companies/franchise/${franchiseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Handle both response formats
      const companiesData = companiesResponse.data.companies || companiesResponse.data;
      console.log('Fetched companies data:', companiesData);
      setCompanies(companiesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch franchise companies.');
    } finally {
      setLoading(false);
    }
  }, [franchiseId]);

  // fetch when component mounts or franchiseId changes
  useEffect(() => {
    fetchFranchiseAndCompanies();
  }, [fetchFranchiseAndCompanies]);

  // Handle Edit Company
  const handleEditCompany = (company) => {
    // Only allow Super Admin to edit
    // Change to (allows both superadmin and super_employee):
    if (!currentUser || !['superadmin', 'super_employee'].includes(currentUser.role)) {
      alert('Only Super Admin or Super Employee can edit companies');
      return;
    }

    // Navigate to Companies page with the company data for editing
    navigate('/companies', {
      state: {
        editingCompany: company
      }
    });
  };

  // Handle form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle weekly holidays checkbox
  const handleWeeklyHolidayChange = (day) => {
    setEditFormData(prev => ({
      ...prev,
      weeklyHolidays: prev.weeklyHolidays.includes(day)
        ? prev.weeklyHolidays.filter(d => d !== day)
        : [...prev.weeklyHolidays, day]
    }));
  };

  // Handle Update Company
  const handleUpdateCompany = async (e) => {
    e.preventDefault();

    if (!currentUser || !['superadmin', 'super_employee'].includes(currentUser.role)) {
      alert('Only Super Admin or Super Employee can update companies');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('No authentication token found');
      return;
    }

    try {
      const updateData = {
        businessName: editFormData.businessName,
        businessEmail: editFormData.businessEmail,
        businessPhone: editFormData.businessPhone,
        emergencyMobile: editFormData.emergencyMobile,
        address: editFormData.address,
        weeklyHolidays: editFormData.weeklyHolidays,
        businessSubscriptionPlan: JSON.stringify({
          title: editFormData.businessSubscriptionPlan,
          startDate: editFormData.startDate,
          endDate: editFormData.endDate
        })
      };

      console.log('Sending update data:', updateData);

      const response = await axios.put(
        `https://slateblue-mallard-430836.hostingersite.com/api/companies/${editingCompany._id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Update response:', response.data);

      alert('Company updated successfully!');
      setShowEditModal(false);
      setEditingCompany(null);

      // Refresh companies list
      console.log('Refreshing companies list...');
      fetchFranchiseAndCompanies();
    } catch (err) {
      console.error('Error updating company:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);

      let errorMessage = 'Failed to update company. ';

      if (err.response) {
        // Server responded with error
        errorMessage += `Status: ${err.response.status}. `;
        errorMessage += err.response.data?.message || err.response.data?.error || 'Unknown server error';
      } else if (err.request) {
        // Request made but no response
        errorMessage += 'No response from server. Make sure backend is running on https://slateblue-mallard-430836.hostingersite.com';
      } else {
        // Error setting up request
        errorMessage += err.message;
      }

      alert(errorMessage);
    }
  };

  // Handle Delete Company
  const handleDeleteCompany = async (companyId) => {
    // Only allow Super Admin to delete
    if (!currentUser || !['superadmin', 'super_employee'].includes(currentUser.role)) {
      alert('Only Super Admin or Super Employee can update companies');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('No authentication token found');
      return;
    }

    try {
      await axios.delete(
        `https://slateblue-mallard-430836.hostingersite.com/api/companies/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('Company deleted successfully!');

      // Refresh companies list
      fetchFranchiseAndCompanies();
    } catch (err) {
      console.error('Error deleting company:', err);
      alert('Failed to delete company: ' + (err.response?.data?.message || err.message));
    }
  };

  // Helper to parse subscription plan safely
  const parseSubscriptionPlan = (plan) => {
    if (!plan) return { title: 'Base Plan' };
    if (typeof plan === 'object') return plan;
    if (typeof plan === 'string') {
      // Check if it looks like a JSON object
      if (plan.trim().startsWith('{')) {
        try {
          return JSON.parse(plan);
        } catch (e) {
          return { title: plan };
        }
      }
      // Otherwise treat string as title
      return { title: plan };
    }
    return { title: 'Base Plan' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading franchise companies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
            <button
              onClick={() => navigate('/franchises')}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Back to Franchises
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/franchises')}
            className="mb-4 flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Franchises
          </button>

          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 shadow-xl text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {franchise?.franchiseLogo ? (
                  <img
                    src={franchise.franchiseLogo}
                    alt="Franchise logo"
                    className="h-16 w-16 object-cover rounded-xl shadow-md mr-4"
                  />
                ) : (
                  <div className="h-16 w-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center shadow-md mr-4">
                    <span className="text-white text-2xl font-bold">
                      {franchise?.franchiseName ? franchise.franchiseName.charAt(0).toUpperCase() : 'F'}
                    </span>
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold">{franchise?.franchiseName || 'Franchise'}</h1>
                  <p className="text-white text-opacity-90 mt-1">Companies Under This Franchise</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">{companies.length}</div>
                <div className="text-sm text-white text-opacity-90">{companies.length === 1 ? 'Company' : 'Companies'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Franchise Details Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 border border-slate-100">
          <h2 className="text-xl font-semibold text-slate-800 mb-4">Franchise Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500">Email</p>
              <p className="font-medium text-slate-900">{franchise?.franchiseEmail || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Phone</p>
              <p className="font-medium text-slate-900">{franchise?.franchisePhone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Address</p>
              <p className="font-medium text-slate-900">{franchise?.address || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Plan Price</p>
              <p className="font-medium text-slate-900">₹{franchise?.planPrice || '0'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Duration</p>
              <p className="font-medium text-slate-900">{franchise?.duration || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">User Limit</p>
              <p className="font-medium text-slate-900">{franchise?.userlimit || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Companies List Header & Toggle */}
        <div className="bg-white rounded-t-2xl shadow-sm border border-slate-100 border-b-0">
          <div className="px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-slate-800">Companies List</h2>
            <div className="flex bg-slate-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-all ${viewMode === 'table' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                title="List View"
              >
                <FiList size={20} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                title="Grid View"
              >
                <FiGrid size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Companies Content */}
        <div className={`bg-white ${viewMode === 'table' ? 'rounded-b-2xl' : 'bg-transparent'} shadow-sm border border-slate-100 ${viewMode === 'grid' ? 'border-0 shadow-none bg-transparent' : ''}`}>

          {companies.length === 0 ? (
            <div className="text-center py-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-b-2xl">
              <svg className="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-slate-600 text-lg font-medium">No companies found under this franchise</p>
              <p className="text-slate-400 text-sm mt-2">Companies created by this franchise will appear here</p>
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <div className="overflow-x-auto rounded-b-2xl">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">SR NO</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">COMPANY NAME</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">COMPANY EMAIL</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">COMPANY PHONE</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">CREATED DATE</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">START DATE</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">END DATE</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">SUBSCRIPTION PLAN</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {companies.map((company, index) => (
                        <tr key={company._id || index} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{index + 1}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-indigo-600">{company.businessName || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{company.businessEmail || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{company.businessPhone || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {company.startDate ? new Date(company.startDate).toLocaleDateString() :
                              (parseSubscriptionPlan(company.businessSubscriptionPlan).startDate ?
                                new Date(parseSubscriptionPlan(company.businessSubscriptionPlan).startDate).toLocaleDateString() : 'N/A')
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {company.endDate ? new Date(company.endDate).toLocaleDateString() :
                              (parseSubscriptionPlan(company.businessSubscriptionPlan).endDate ?
                                new Date(parseSubscriptionPlan(company.businessSubscriptionPlan).endDate).toLocaleDateString() : 'N/A')
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {parseSubscriptionPlan(company.businessSubscriptionPlan).title || 'Base Plan'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleEditCompany(company)}
                              className="text-indigo-600 hover:text-indigo-900 mr-4"
                              title={!['superadmin', 'super_employee'].includes(currentUser?.role)
                                ? 'Only Super Admin/Super Employee can edit'
                                : 'Edit company'}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCompany(company._id)}
                              className="text-red-600 hover:text-red-900"
                              title={!['superadmin', 'super_employee'].includes(currentUser?.role)
                                ? 'Only Super Admin/Super Employee can delete'
                                : 'Delete company'}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {companies.map((company, index) => (
                    <div key={company._id || index} className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300 overflow-hidden group">
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center">
                            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-md">
                              {company.businessName ? company.businessName.charAt(0).toUpperCase() : 'C'}
                            </div>
                            <div className="ml-4">
                              <h3 className="font-bold text-lg text-slate-800 group-hover:text-indigo-600 transition-colors truncate max-w-[150px]">
                                {company.businessName || 'N/A'}
                              </h3>
                              <span className="text-xs font-medium px-2 py-1 bg-green-50 text-green-600 rounded-lg">
                                {parseSubscriptionPlan(company.businessSubscriptionPlan).title || 'Base Plan'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3 mb-6">
                          <div className="flex items-center text-slate-500 text-sm">
                            <FiMail className="mr-3 text-indigo-400 flex-shrink-0" />
                            <span className="truncate">{company.businessEmail || 'No email'}</span>
                          </div>
                          <div className="flex items-center text-slate-500 text-sm">
                            <FiPhone className="mr-3 text-indigo-400 flex-shrink-0" />
                            <span>{company.businessPhone || 'No phone'}</span>
                          </div>
                          <div className="flex items-center text-slate-500 text-sm">
                            <FiCalendar className="mr-3 text-indigo-400 flex-shrink-0" />
                            <span>Created: {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : 'N/A'}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 py-3 border-t border-slate-50">
                          <div className="bg-slate-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Start Date</p>
                            <p className="text-xs font-semibold text-slate-700">
                              {company.startDate ? new Date(company.startDate).toLocaleDateString() :
                                (parseSubscriptionPlan(company.businessSubscriptionPlan).startDate ?
                                  new Date(parseSubscriptionPlan(company.businessSubscriptionPlan).startDate).toLocaleDateString() : 'N/A')
                              }
                            </p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">End Date</p>
                            <p className="text-xs font-semibold text-slate-700">
                              {company.endDate ? new Date(company.endDate).toLocaleDateString() :
                                (parseSubscriptionPlan(company.businessSubscriptionPlan).endDate ?
                                  new Date(parseSubscriptionPlan(company.businessSubscriptionPlan).endDate).toLocaleDateString() : 'N/A')
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                        <button
                          onClick={() => handleEditCompany(company)}
                          className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                          title={currentUser?.role !== 'superadmin' ? 'Only Super Admins can edit' : 'Edit company'}
                        >
                          <FiEdit2 className="mr-2" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCompany(company._id)}
                          className="flex items-center text-sm font-medium text-red-500 hover:text-red-600 transition-colors"
                          title={currentUser?.role !== 'superadmin' ? 'Only Super Admins can delete' : 'Delete company'}
                        >
                          <FiTrash2 className="mr-2" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Edit Company Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl z-10">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold">Edit Company</h2>
                    <p className="text-white/80 text-sm mt-1">Update company information</p>
                  </div>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <FiX size={24} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <form onSubmit={handleUpdateCompany} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Company Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center">
                      Company Name <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={editFormData.businessName}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                      placeholder="Enter company name"
                      required
                    />
                  </div>

                  {/* Subscription Plan */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Subscription Plan</label>
                    <select
                      name="businessSubscriptionPlan"
                      value={editFormData.businessSubscriptionPlan}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                    >
                      <option value="">Select Plan</option>
                      <option value="Base Plan">Base Plan</option>
                      <option value="7-Day Trial">7-Day Trial</option>
                      <option value="Plan Test">Plan Test</option>
                      <option value="my plan">My Plan</option>
                      <option value="simple">Simple</option>
                    </select>
                  </div>

                  {/* Company Email */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center">
                      Company Email <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      name="businessEmail"
                      value={editFormData.businessEmail}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                      placeholder="company@example.com"
                      required
                    />
                  </div>

                  {/* Company Logo (Display only) */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Company Logo</label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-200 transition-all text-sm"
                      >
                        Choose File
                      </button>
                      <span className="text-sm text-slate-400">No file chosen</span>
                    </div>
                  </div>

                  {/* Company Phone */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Company Phone</label>
                    <input
                      type="tel"
                      name="businessPhone"
                      value={editFormData.businessPhone}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                      placeholder="Enter phone number"
                    />
                  </div>

                  {/* Created Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Created Date</label>
                    <input
                      type="date"
                      name="createdDate"
                      value={editFormData.createdDate}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                      disabled
                    />
                  </div>

                  {/* Emergency Mobile */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Emergency Mobile</label>
                    <input
                      type="tel"
                      name="emergencyMobile"
                      value={editFormData.emergencyMobile}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                      placeholder="Emergency contact number"
                    />
                  </div>

                  {/* Start Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={editFormData.startDate}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                    />
                  </div>

                  {/* Password (Display only - cannot be changed) */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Password</label>
                    <input
                      type="password"
                      value="Password cannot be changed"
                      className="w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 cursor-not-allowed"
                      disabled
                    />
                    <p className="text-xs text-slate-400 italic">Password cannot be changed after creation for security reasons.</p>
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={editFormData.endDate}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Weekly Holidays */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">Weekly Holidays</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <label key={day} className="flex items-center space-x-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={editFormData.weeklyHolidays.includes(day)}
                          onChange={() => handleWeeklyHolidayChange(day)}
                          className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-800">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Address</label>
                  <textarea
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditInputChange}
                    rows="3"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all resize-none"
                    placeholder="Enter company address"
                  ></textarea>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:opacity-90 transition-all font-medium"
                  >
                    Update Company
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FranchiseCompanies;


